"use client";

import { useState, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import { Undo2, Redo2 } from "lucide-react";
import { Input, Textarea, IconButton } from "@/ui";
import { PromptMenu } from "./PromptMenu";
import { ENHANCEMENT_OPTIONS } from "../enhancementPrompts";
import ImproveAIButton from "@components/shared/form/ai/ImproveAIButton";
import RewriteWithAIPanel from "@components/shared/form/ai/RewriteWithAIPanel";
import toolbarStyles from "@components/shared/form/ai/RichAIField/RichAIField.module.css";
import styles from "./AITextField.module.css";

const cx = (...classNames) => classNames.filter(Boolean).join(" ");

const ENHANCEMENT_LABELS = Object.fromEntries(
  ENHANCEMENT_OPTIONS.map((option) => [option.id, option.label])
);

// The native maxLength attribute only stops typing; an AI rewrite is set
// programmatically, so it's trimmed here instead — at the last word break
// that fits, so it doesn't end mid-word.
const fitToLength = (text, maxLength) => {
  if (!maxLength || text.length <= maxLength) return text;
  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim();
};

export function AITextField({
  // Standard props
  // Coerced below with `?? ""` — the default here only catches `undefined`,
  // but callers can pass `null` (e.g. a role with no description column).
  value: rawValue = "",
  onChange,
  placeholder = "",
  disabled = false,
  name,
  id,

  // InputAI specific props
  variant = "auto",
  autoExpandThreshold = 100,

  // AI Configuration
  prompt = "",
  excludePrompts = [],

  // Field props
  label,
  headerActions,
  helperText,
  error = false,
  required = false,
  fullWidth = true,
  size = "medium",

  // Hides the "AI: <label>" version chip and undo/redo controls that appear
  // above the field after an enhancement is applied.
  hideHistoryControls = false,

  // Renders the enhance trigger as a small pill button inside the right edge
  // of the field instead of the full-width bar underneath it. Only applies
  // to the single-line Input (variant "input" / "auto" below the threshold).
  compact = false,

  // Textarea props
  minRows = 1,
  maxRows = 10,
  maxLength,

  // Callbacks
  onEnhancementStart,
  onEnhancementComplete,
  onEnhancementError,

  // Called with the current value once the user picks a prompt from the
  // menu, before the AI request fires. Return a string to use as the
  // "enhanced" result directly (skipping the API call) — e.g. to swap in a
  // fallback instead of sending gibberish off to be "improved".
  onBeforeEnhance,

  // Pulled out of ...fieldProps (rather than left to the native HTML
  // autofocus attribute) so the effect below can also force the caret —
  // and the field's scroll position — to the start. Autofocusing a
  // pre-filled input natively tends to land the browser's caret at the
  // END of the value instead, scrolling long text out of view.
  autoFocus = false,

  // Other props
  ...fieldProps
}) {
  const value = rawValue ?? "";

  const [focused, setFocused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [userCustomPrompt, setUserCustomPrompt] = useState("");
  const [history, setHistory] = useState({ versions: [], index: 0 });

  const fieldRef = useRef(null);
  const wrapperRef = useRef(null);
  // Anchors the "Rewrite with AI" panel to the toolbar's action group (the
  // Improve button itself) rather than the whole field, and lets the panel
  // portal to document.body — same pattern as RichTextAIField.js — so it
  // always opens right under the button and escapes any clipping ancestor.
  const toolbarActionsRef = useRef(null);
  const rewritePanelRef = useRef(null);

  useEffect(() => {
    if (!autoFocus) return;
    const el = fieldRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange?.(0, 0);
    el.scrollLeft = 0;
  }, [autoFocus]);

  // In "auto" mode the threshold decides between an Input and a Textarea, but
  // swapping element types mid-keystroke unmounts the focused field and drops
  // the caret. Latch the choice and only re-evaluate it while unfocused, so
  // the swap lands on blur instead.
  const [autoExpanded, setAutoExpanded] = useState(
    () => value.length > autoExpandThreshold
  );

  // Re-evaluated only at the two moments the field isn't being typed into:
  // when it loses focus, and when an enhancement replaces the text wholesale.
  const reevaluateAutoExpand = (nextValue) =>
    setAutoExpanded(nextValue.length > autoExpandThreshold);

  const shouldUseTextarea =
    variant === "textarea" || (variant === "auto" && autoExpanded);

  const canUndo = history.index > 0;
  const canRedo = history.index < history.versions.length - 1;
  const totalVersions = history.versions.length;

  const handleEnhanceClick = () => {
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  const handleEnhancementSelect = async (enhancementType) => {
    handleCloseMenu();
    await performEnhancement(enhancementType);
  };

  const handleCustomEnhancement = async () => {
    if (userCustomPrompt.trim()) {
      setMenuOpen(false);
      await performEnhancement("custom", userCustomPrompt);
    }
  };

  const handleUpdateCustomPrompt = (event) => {
    setUserCustomPrompt(event.target.value);
  };

  const performEnhancement = async (enhancementType, customPromptText = "") => {
    if (!value.trim()) return;

    const override = onBeforeEnhance?.(value);
    if (override) {
      handleAcceptEnhancement(override, enhancementType);
      return;
    }

    setIsEnhancing(true);
    onEnhancementStart?.();

    try {
      const response = await fetch("/api/enhance-text", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text: value,
          enhancementType,
          prompt: prompt || "General text improvement",
          customPrompt: customPromptText,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Enhancement failed with status ${response.status}`
        );
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let enhancedText = "";

      while (true) {
        const { done, value: chunk } = await reader.read();
        if (done) break;
        enhancedText += decoder.decode(chunk, { stream: true });
      }
      // Flush any multi-byte character straddling the final chunk boundary.
      enhancedText += decoder.decode();

      const cleanedText = fitToLength(enhancedText.trim(), maxLength);

      if (cleanedText) {
        handleAcceptEnhancement(cleanedText, enhancementType);
      } else {
        throw new Error("No enhanced text received");
      }
    } catch (enhancementError) {
      console.error("Enhancement error:", enhancementError);
      onEnhancementError?.(enhancementError);
      toast.error(
        enhancementError.message || "Enhancement failed. Please try again."
      );
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleAcceptEnhancement = (enhancedText, enhancementType) => {
    const versionLabel =
      enhancementType === "custom"
        ? "Custom prompt"
        : ENHANCEMENT_LABELS[enhancementType] || "Enhanced";

    setHistory((prev) => {
      const versions = prev.versions.length
        ? prev.versions
        : [{ text: value, label: "Original" }];
      const truncated = versions.slice(0, prev.index + 1);
      const nextVersions = [
        ...truncated,
        { text: enhancedText, label: versionLabel },
      ];
      return { versions: nextVersions, index: nextVersions.length - 1 };
    });

    reevaluateAutoExpand(enhancedText);
    onChange?.(enhancedText);
    onEnhancementComplete?.(value, enhancedText);
  };

  const handleUndo = () => {
    if (!canUndo) return;
    const newIndex = history.index - 1;
    onChange?.(history.versions[newIndex].text);
    setHistory((prev) => ({ ...prev, index: newIndex }));
  };

  const handleRedo = () => {
    if (!canRedo) return;
    const newIndex = history.index + 1;
    onChange?.(history.versions[newIndex].text);
    setHistory((prev) => ({ ...prev, index: newIndex }));
  };

  // Deliberately no Ctrl/Cmd+Z binding here. History only tracks enhancement
  // results, not typing, so hijacking undo meant a user who hand-edited an
  // enhanced value and pressed Cmd+Z lost those edits to the pre-enhancement
  // text. Native undo stays with the field; version stepping lives on the
  // undo/redo buttons, which say what they step through.
  useEffect(() => {
    if (!focused) return undefined;

    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "e") {
        event.preventDefault();
        handleEnhanceClick();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [focused]);

  // Close the "Rewrite with AI" panel when focus or a click moves outside
  // this field — covers both tabbing away (focusin; blur doesn't bubble)
  // and clicking into a different section (mousedown).
  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleOutside = (event) => {
      const insideWrapper = wrapperRef.current?.contains(event.target);
      const insideRewritePanel = rewritePanelRef.current?.contains(event.target);
      if (!insideWrapper && !insideRewritePanel) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("focusin", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("focusin", handleOutside);
    };
  }, [menuOpen]);

  const historyControls = !hideHistoryControls && (canUndo || canRedo) && !isEnhancing && (
    <div className={styles.historyControls}>
      {totalVersions > 1 && (
        <span className={styles.versionChip}>
          AI: {history.versions[history.index].label}
        </span>
      )}
      <IconButton
        size="sm"
        type="button"
        className={styles.undoButton}
        onClick={handleUndo}
        disabled={!canUndo}
        title="Undo last enhancement (Ctrl+Z)"
      >
        <Undo2 size={14} />
      </IconButton>
      <IconButton
        size="sm"
        type="button"
        className={styles.undoButton}
        onClick={handleRedo}
        disabled={!canRedo}
        title="Redo next enhancement (Ctrl+Shift+Z)"
      >
        <Redo2 size={14} />
      </IconButton>
    </div>
  );

  const skeletonLineWidths = shouldUseTextarea
    ? ["100%", "92%", "96%", "60%", "100%", "88%", "94%", "40%"]
    : ["70%"];

  const sharedProps = {
    ref: fieldRef,
    value,
    onFocus: () => setFocused(true),
    onBlur: () => {
      setFocused(false);
      reevaluateAutoExpand(value);
    },
    placeholder,
    disabled: disabled || isEnhancing,
    name,
    id,
    helperText,
    error,
    required,
    maxLength,
    ...fieldProps,
  };

  return (
    <div
      ref={wrapperRef}
      className={styles.wrapper}
      style={{ width: fullWidth ? "100%" : "auto" }}
    >
      {(label || headerActions || historyControls) && (
        <div className={styles.labelRow}>
          {label && (
            <label className={styles.fieldLabel} htmlFor={id}>
              {label}
              {required && <span className={styles.fieldLabelRequired}>*</span>}
            </label>
          )}
          <div className={styles.headerActionsGroup}>
            {headerActions}
            {historyControls}
          </div>
        </div>
      )}
      <div className={styles.fieldWrapper}>
        {shouldUseTextarea ? (
          <Textarea
            {...sharedProps}
            onChange={(newValue) => onChange?.(newValue)}
            rows={minRows}
            style={{ maxHeight: `${maxRows * 24}px` }}
          />
        ) : (
          <Input
            {...sharedProps}
            fullWidth={fullWidth}
            size={size === "small" ? "sm" : size === "large" ? "lg" : "md"}
            onChange={(event) => onChange?.(event.target.value)}
            endIcon={
              compact ? (
                <div className={toolbarStyles.aiActions} ref={toolbarActionsRef}>
                  {canUndo && (
                    <button
                      type="button"
                      className={cx(toolbarStyles.formatButton, toolbarStyles.interactive)}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={handleUndo}
                      title="Undo last AI change"
                    >
                      <Undo2 size={16} />
                    </button>
                  )}
                  <ImproveAIButton
                    active={menuOpen}
                    onClick={() => setMenuOpen((open) => !open)}
                  />
                </div>
              ) : undefined
            }
          />
        )}
        {isEnhancing && (
          <div
            className={cx(
              styles.skeletonOverlay,
              shouldUseTextarea && styles.skeletonOverlayMultiline
            )}
            aria-hidden="true"
          >
            {skeletonLineWidths.map((width, index) => (
              <span
                key={index}
                className={styles.skeletonLine}
                style={{ width }}
              />
            ))}
          </div>
        )}
        {compact && menuOpen && (
          <RewriteWithAIPanel
            onClose={handleCloseMenu}
            onOptionSelect={(enhancementType) => performEnhancement(enhancementType)}
            onCustomSubmit={(customPrompt) => performEnhancement("custom", customPrompt)}
            isEnhancing={isEnhancing}
            excludePrompts={excludePrompts}
            anchorRef={toolbarActionsRef}
            panelRef={rewritePanelRef}
          />
        )}
      </div>

      {!compact && !menuOpen && (
        <div className={styles.solaBar}>
          <button
            type="button"
            className={styles.improveButton}
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleEnhanceClick}
          >
            <img
              src="/img/floats/sola.svg"
              width="16"
              height="16"
              alt=""
              className={styles.solaIcon}
            />
            Improve with SOLA AI
          </button>
        </div>
      )}
      {!compact && menuOpen && (
        <div className={styles.solaBar}>
          <PromptMenu
            onClose={handleCloseMenu}
            onEnhancementSelect={handleEnhancementSelect}
            userCustomPrompt={userCustomPrompt}
            onCustomPromptChange={handleUpdateCustomPrompt}
            onCustomEnhancement={handleCustomEnhancement}
            excludePrompts={excludePrompts}
          />
        </div>
      )}
    </div>
  );
}
